import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import { DiscrepancySeverity, ReconciliationState, UnitType } from "generated-graphql/graphql";

import { formatQuantity, NO_VALUE_PLACEHOLDER } from "./shipmentDisplay";

/**
 * Turning the reconciliation the server computes into something readable.
 *
 * The server deliberately sends normalized identifiers and unformatted values —
 * a field key, a bare number in the unit the row names, a JSON array of codes —
 * so the comparison stays the same shape everywhere and the wording lives here.
 * That means every label in this file is a display decision, not data.
 *
 * Quantities arrive in POUNDS. They are compared in kilograms internally, but
 * pounds is what the manifest surfaces speak, so the conversion happens server
 * side and the row carries its own unit rather than the client assuming one.
 */

/**
 * Plain descriptions of the situation rather than names for the state. The waste
 * designs favour this voice, and it keeps one vocabulary across the surfaces:
 * the facility tile counts "shipments differing from manifest" and the panel
 * says the manifest "matches this request", so the chip should not be the only
 * place that calls the same thing "discrepant".
 */
export const RECONCILIATION_LABELS: Record<ReconciliationState, string> = {
  [ReconciliationState.None]: "No manifest",
  [ReconciliationState.InSync]: "Matches request",
  [ReconciliationState.Discrepant]: "Differs from request",
  [ReconciliationState.NotReviewed]: "Manifest not reviewed",
};

/**
 * A discrepancy is not a failure — the vendor legitimately re-weighs,
 * re-classifies and splits lines — so `DISCREPANT` is a warning to look at,
 * never an error. `NOT_REVIEWED` is quieter still: it reports an outstanding
 * review rather than anything being wrong.
 */
export const RECONCILIATION_CHIP_COLORS: Record<
  ReconciliationState,
  "default" | "info" | "success" | "warning"
> = {
  [ReconciliationState.None]: "default",
  [ReconciliationState.InSync]: "success",
  [ReconciliationState.Discrepant]: "warning",
  // Not a warning: nothing is wrong with the shipment, there is simply a
  // review outstanding before anything can be said about it.
  [ReconciliationState.NotReviewed]: "info",
};

export const SEVERITY_LABELS: Record<DiscrepancySeverity, string> = {
  [DiscrepancySeverity.Minor]: "Minor",
  [DiscrepancySeverity.Moderate]: "Moderate",
  [DiscrepancySeverity.Critical]: "Critical",
};

export const SEVERITY_CHIP_COLORS: Record<DiscrepancySeverity, "info" | "warning" | "error"> = {
  [DiscrepancySeverity.Minor]: "info",
  [DiscrepancySeverity.Moderate]: "warning",
  [DiscrepancySeverity.Critical]: "error",
};

/**
 * The field identifiers `ShipmentFieldDiff.field` can carry. `quantity` and the
 * two code lists are per-stream comparisons; `manifestNotReviewed` is a
 * shipment-level note that nothing could be compared yet.
 */
export const SHIPMENT_DIFF_FIELD_LABELS: Record<string, string> = {
  quantity: "Quantity",
  federalWasteCodes: "Federal waste codes",
  stateWasteCodes: "State waste codes",
  manifestNotReviewed: "Manifest not reviewed",
};

/**
 * Fields that describe the shipment rather than any one waste stream.
 *
 * The discriminator is the FIELD, not a null `wasteStreamId`: null is also the
 * uncharacterized bucket's own identity, so a comparison of waste with no
 * stream assigned carries a null id while being a real per-stream row.
 */
export const SHIPMENT_LEVEL_DIFF_FIELDS = new Set(["manifestNotReviewed"]);

export function isShipmentLevelDiff(field: string): boolean {
  return SHIPMENT_LEVEL_DIFF_FIELDS.has(field);
}

export function diffFieldLabel(field: string): string {
  return SHIPMENT_DIFF_FIELD_LABELS[field] ?? field;
}

/**
 * What a non-discrepant row means. These rows report a limit on what could be
 * checked rather than a disagreement, and saying so is the whole point — an
 * empty differences table and an unverifiable one look identical otherwise.
 */
export const DIFF_LIMITATION_NOTES: Record<string, string> = {
  quantity:
    "Quantities could not be compared — at least one side has no weight conversion, " +
    "or a total did not cover every line.",
  federalWasteCodes: "Federal waste codes could not be compared on this stream.",
  stateWasteCodes: "State waste codes could not be compared on this stream.",
  manifestNotReviewed:
    "This manifest has no reviewed source yet, so its values are not treated as " +
    "the record. Reconciliation runs again once the document is reviewed.",
};

export function diffLimitationNote(field: string): string {
  return DIFF_LIMITATION_NOTES[field] ?? "This field could not be compared.";
}

/**
 * Render one side of a diff.
 *
 * Absent and empty are different answers and must not collapse into the same
 * text: a stream the other side never mentioned sends null, while a stream that
 * genuinely carries no codes sends "[]". Reading "None" for both would turn a
 * missing line into a line with no codes.
 */
export function formatDiffValue(
  field: string,
  value: string | null | undefined,
  /** The unit the server expressed this value in; null for unitless fields. */
  unit?: UnitType | null
): string {
  if (value == null) {
    return NO_VALUE_PLACEHOLDER;
  }
  if (field === "federalWasteCodes" || field === "stateWasteCodes") {
    return formatCodeList(value);
  }
  if (unit) {
    const amount = safeParseFloat(value);
    return amount == null ? value : formatQuantity(amount, unit);
  }
  return value;
}

/**
 * Code lists arrive as a JSON array. A value that does not parse is shown as
 * sent rather than swallowed — a malformed list is worth seeing, and inventing
 * "None" for it would hide a real data problem.
 */
function formatCodeList(value: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return value;
  }
  if (!Array.isArray(parsed)) {
    return value;
  }
  if (parsed.length === 0) {
    return "None";
  }
  return parsed.map((code) => String(code)).join(", ");
}

/**
 * Why a manifest is attached but has no total to state.
 *
 * Three different reasons, and the difference matters to a reader deciding
 * whether to act: nothing readable yet, no record to read, or a load that cannot
 * be weighed onto one scale. Only the first has a chip beside it saying so.
 *
 * Takes the two fields it reads rather than a whole shipment, so the branch a
 * reader depends on can be tested without standing up a shipment to do it.
 */
export function missingManifestTotalReason(shipment: {
  reconciliation: { status: ReconciliationState };
  /**
   * The attached manifest record, or null when the row is mid-rebuild. Optional
   * because that is how the generated type spells a nullable field; absent reads
   * the same as null, which is safe here because the detail query always selects
   * it.
   */
  manifest?: unknown;
}): string {
  if (shipment.reconciliation.status === ReconciliationState.NotReviewed) {
    return "Awaiting review";
  }
  // The pipeline deleted the manifest row and has not rebuilt it yet; the
  // tracking number survives from the shipment's own snapshot.
  if (shipment.manifest == null) {
    return "Record rebuilding";
  }
  // A stream measured in a unit with no weight conversion, or a total that could
  // not cover every line. Reporting the half it can weigh would understate it.
  return "No comparable total";
}
